import {
  Card,
  Text,
  InlineStack,
  Link,
  Button,
  BlockStack,
  Modal,
  ResourceList,
  ResourceItem,
  Box,
  InlineGrid,
  Icon,
  Select,
  EmptyState,
  TextField,
  Collapsible,
  Divider
} from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { usePartnerUp } from '../providers/PartnerUpStoreProvider';
import { EmailFollowUpIcon, ArchiveIcon, EditIcon, ArrowLeftIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon, ClockIcon } from '@shopify/polaris-icons';
import AppLayout from '../components/layout';
import InstructionsRichTextEditor from '../components/InstructionsRichTextEditor';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';

const sequenceTemplates = {
  'Hello Emails': {
    name: 'New Hello Sequence',
    template: 'Hello Emails',
    steps: [{
      subject: `We're looking for Influencers at {storeName}, Are You Available?`,
      body: `<p>Hi {firstName}</p>
<p>I'm the founder at {storeName} - I saw your posts on social media and was really impressed</p>
<br>
<p>We're a fast growing brand that...</p>
<br>
<p>We have a network of creators who are earning commission rewards and tons of free products.</p>
<br>
<p>
Can we send you a box of products, and an affiliate code for a post? 
</p>
<br>
<p>Just send me a quick reply and I'll send all the details.</p>
<p>{signature}</p>`,
      internalName: 'Initial Invite',
      delay: 0
    }, {
      subject: `We're looking for Influencers at {storeName}, Are You Available?`,
      body: `<p>Hi {firstName},</p><p><br></p><p>Just wanted to follow up on my last email</p><p><br></p><p>Would be great to speak about some form of collaboration!</p><p><br></p><p>Just send me a quick reply if you're interested. Thank you!</p><p>Thanks,</p><p>{fromName}</p>`,
      internalName: 'Reminder 1',
      delay: 3,
    }, {
      subject: `We're looking for Influencers at {storeName}, Are You Available?`,
      body: `<p>Hi {firstName},</p><p><br></p><p>Just one more very quick follow up as I'm sure you're busy</p><p><br></p><p>Let me know if you're at all interested in connecting with our brand.</p><br><p>Thanks,</p><p>{fromName}</p>`,
      internalName: 'Reminder 2',
      delay: 3,
    }],
    timeZone: 'America/Los_Angeles (UTC-8)',
    timeToSend: '9:00 AM',
  },
  'Send Contract': {
    name: 'New Send Contract Sequence',
    template: 'Send Contract',
    steps: [{
      subject: `Here's Your Contract, {firstName}`,
      body: `<p>Hi {firstName},</p><p><br></p><p>We'd like to invite you to join our new marketing campaign.</p><p><br></p><p>Here are the details:</p><p>{campaignDetails}</p><br><p>If you're up for it, please fill out the short form below:</p><p>{campaignLink}</a></p><p><br></p><p>Thanks,</p><p>{fromName}</p>`,
      internalName: 'Initial Invite',
      delay: 0
    }, {
      subject: `Here's Your Contract, {firstName}`,
      body: `<p>Hi {firstName}</p><p><br></p><p>Just wanted to send a quick follow-up on the previous marketing campaign invite.</p><p><br></p><p>We think it could be a great partnership!</p><p><br></p><p>Here is the sign up link, it should just take a minute.</p><p>{campaignLink}</p><p><br></p><p>Best</p><p>{fromName}</p>`,
      internalName: 'Reminder 1',
      delay: 3
    }, {
      subject: `Here's Your Contract, {firstName}`,
      body: `<p>Hi {firstName}</p><p><br></p><p>In case you missed it, one final reminder about the campaign we're launching with creators like you.</p><p><br></p><p>Here are the details again</p><p>{campaignDetails}</p><p><br></p><p>And here is the sign up link:</p><p><strong>{campaignLink}</strong></p><p><br></p><p>Best</p><p>{fromName}</p>`,
      internalName: 'Reminder 2',
      delay: 3
    }],
    timeZone: 'America/Los_Angeles (UTC-8)',
    timeToSend: '9:00 AM',
  },
  'Contract Submitted': {
    name: 'New Contract Submitted Sequence',
    template: 'Contract Submitted',
    steps: [{
      subject: `Congrats! You were accepted for {campaignName} with {storeName}!`,
      body: `<p>Hi {firstName},</p><p><br></p><p>Congrats!</p><p><br></p><p>You were accepted for our campaign: {campaignName} as a partner!</p><p><br></p><p><strong>Here are the agreed upon details:</strong></p><p>{campaignDetails}</p><p><br></p><p><br></p><p>Finally, using this portal link, you can upload content for review, track payments, or respond to messages:</p><p>{portalLink}</p><p><br></p><p>(Just log in with the same email as we're sending this message to)</p><p><br></p><p>Thanks! And feel free to reply with any questions</p><p>{fromName}</p>`,
      internalName: 'On Invite Accepted',
      delay: 0
    }],
    timeZone: 'America/Los_Angeles (UTC-8)',
    timeToSend: '9:00 AM',
  },
  'Custom': {
    name: 'New Custom Sequence',
    template: 'Custom',
    steps: [{
      subject: ``,
      body: ``,
      internalName: 'Step 1',
      delay: 0
    }],
    timeZone: 'America/Los_Angeles (UTC-8)',
    timeToSend: '9:00 AM',
  }
}

export default function Sequences({ defaultSequenceType, onClickSequence, selected = null, removeSelected, showStats = true, showActions = true, smallTable = false }) {
  const { emailSequences, refetchEmailSequences, setToast, store, db } = usePartnerUp();
  const [archiveAreYouSure, setArchiveAreYouSure] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [stage, setStage] = useState('table') // table | chooseTemplate | editSequence
  const [stepOpen, setStepOpen] = useState(0)
  const [localSequence, setLocalSequence] = useState({})
  const [sequenceTemplateOverride, setSequenceTemplateOverride] = useState(null)

  const [saveLoading, setSaveLoading] = useState(false)

  const [sequenceType, setSequenceType] = useState(defaultSequenceType || 'All')

  const resourceName = {
    singular: 'sequence',
    plural: 'sequences',
  };

  const variables = [
    { name: 'First Name', value: '{firstName}' },
    { name: 'Store Name', value: store?.name || '{storeName}' },
    { name: 'Signature', value: '{signature}' },
    { name: 'Portal Link', value: '{portalLink}' },
  ];
  if (sequenceTemplateOverride === 'Invite To Campaign') {
    variables.push(
      { name: 'Campaign Name', value: '{campaignName}' },
      { name: 'Campaign Details', value: '{campaignDetails}' },
      { name: 'Campaign Form Link', value: '{campaignLink}' },
    )
  } else if (sequenceTemplateOverride === 'After Campaign Accepted') {
    variables.push(
      { name: 'Campaign Name', value: '{campaignName}' },
      { name: 'Campaign Details', value: '{campaignDetails}' },
    )
  }

  if (selected) {
    return (
      <Card padding="0">
        <ResourceList
          resourceName={resourceName}
          items={[emailSequences?.find(s => s.id === selected)]}
          renderItem={sequence => renderItem(sequence)}
          showHeader={false}
        />
      </Card>
    )
  }
  if (stage === 'chooseTemplate') {
    return (
      <AppLayout>
        <BlockStack gap="500">
          <InlineStack align="start" gap="300" blockAlign="center">
            <Button icon={ArrowLeftIcon} onClick={() => setStage('table')}></Button>
            <Text variant="headingLg">Choose from a Template, or Start from Scratch</Text>
          </InlineStack>
          <InlineGrid gap="300" columns="4">
            <Box onClick={() => {
              setSequenceTemplateOverride('Hello Emails')
              setLocalSequence(sequenceTemplates['Hello Emails'])
              setStage('editSequence')
            }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Hello Emails</Text>
                  <Text>Personalized first contact, with creators you want to work with</Text>
                  <Button>Select</Button>
                </BlockStack>
              </Card>
            </Box>
            <Box onClick={() => {
              setSequenceTemplateOverride('Send Contract')
              setLocalSequence(sequenceTemplates['Send Contract'])
              setStage('editSequence')
            }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Send Contract</Text>
                  <Text>Autofill contract details & send personalized links, with reminders</Text>
                  <Button>Select</Button>
                </BlockStack>
              </Card>
            </Box>
            <Box onClick={() => {
              setSequenceTemplateOverride('Contract Submitted')
              setLocalSequence(sequenceTemplates['Contract Submitted'])
              setStage('editSequence')
            }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Accepted Contract</Text>
                  <Text>Provide details, and send reminders, after a contract is accepted</Text>
                  <Button>Select</Button>
                </BlockStack>
              </Card>
            </Box>
            <Box onClick={() => {
              setSequenceTemplateOverride('Custom')
              setLocalSequence(sequenceTemplates['Custom'])
              setStage('editSequence')
            }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Blank</Text>
                  <Text>Generic, blank starting point to customize to your needs</Text>
                  <Button>Select</Button>
                </BlockStack>
              </Card>
            </Box>
          </InlineGrid>
        </BlockStack>
      </AppLayout>
    )
  }

  console.log('localSequence', localSequence)
  if (stage === 'editSequence') {
    return (
      <AppLayout>
        <BlockStack gap="500">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack align="start" gap="100" blockAlign="center">
              <Button icon={ArrowLeftIcon} onClick={() => setStage('table')}></Button>
              <Text variant="headingLg">Sequence Details</Text>
            </InlineStack>
            <Button loading={saveLoading} variant="primary" onClick={async () => {
              try {
                setSaveLoading(true)
                if (localSequence.id) {
                  await setDoc(doc(db, 'emailSequences', localSequence.id), localSequence, { merge: true })
                  refetchEmailSequences()
                  setToast({
                    content: 'Saved Sequence',
                  })
                } else {
                  console.log('adding sequence', localSequence)
                  await addDoc(collection(db, 'emailSequences'), { ...localSequence, shop: store.id })
                  refetchEmailSequences()
                  setStage('table')
                  setToast({
                    content: 'Saved Sequence',
                  })
                }
              } catch (e) {
                console.error('failed to save', e)
                setToast({
                  content: 'Failed to Save. Please try again.',
                  error: true,
                })
              } finally {
                setSaveLoading(false)
              }
            }}>Save</Button>
          </InlineStack>
          <InlineGrid columns="4" gap="500">
            <TextField label="Sequence Name" value={localSequence?.name} onChange={value => setLocalSequence({ ...localSequence, name: value })} />
            <Select label="Sequence Type" options={[
              "Hello Emails",
              "Send Contract",
              "Contract Submitted",
              "Custom"
            ]} value={sequenceTemplateOverride || localSequence?.template} onChange={value => setSequenceTemplateOverride(value)} />
            <Select label="Time Zone" options={[
              "Pacific/Honolulu (UTC-10)",   // UTC-10
              "America/Anchorage (UTC-9)",  // UTC-9
              "America/Los_Angeles (UTC-8)",// UTC-8
              "America/Denver (UTC-7)",     // UTC-7
              "America/Chicago (UTC-6)",    // UTC-6
              "America/New_York (UTC-5)",   // UTC-5
              "America/Halifax (UTC-4)",    // UTC-4
              "Europe/London (UTC+0)",      // UTC+0
              "Europe/Paris (UTC+1)",       // UTC+1
              "Europe/Athens (UTC+2)",      // UTC+2
              "Europe/Istanbul (UTC+3)",    // UTC+3
            ]} value={localSequence.timeZone} onChange={value => setLocalSequence({ ...localSequence, timeZone: value })} />
            <Select label="Time to Send" options={[
              "12:00 AM",
              "1:00 AM",
              "2:00 AM",
              "3:00 AM",
              "4:00 AM",
              "5:00 AM",
              "6:00 AM",
              "7:00 AM",
              "8:00 AM",
              "9:00 AM",
              "10:00 AM",
              "11:00 AM",
              "12:00 PM",
              "1:00 PM",
              "2:00 PM",
              "3:00 PM",
              "4:00 PM",
              "5:00 PM",
              "6:00 PM",
              "7:00 PM",
              "8:00 PM",
              "9:00 PM",
              "10:00 PM",
              "11:00 PM",
            ]} value={localSequence?.timeToSend} onChange={value => setLocalSequence({ ...localSequence, timeToSend: value })} />
          </InlineGrid>
          {
          }
          {
            localSequence?.steps?.map((step, index) => {
              return (
                <Card>
                  <BlockStack gap="4">
                    <InlineStack align="space-between" blockAlign="start">
                      <BlockStack gap="1">
                        <Text variant="headingSm" tone="subdued">Step {index + 1}</Text>
                        {
                          stepOpen !== index ? (
                            <BlockStack gap="100" blockAlign="center">
                              <Text variant="headingMd">{step.internalName}</Text>
                              {
                                index !== 0 ? (
                                  <InlineStack blockAlign="center" gap="100">
                                    <Icon source={ClockIcon} />
                                    <Text variant="bodySm">{step.delay} Days Later</Text>
                                  </InlineStack>
                                ) : null
                              }
                            </BlockStack>
                          ) : null
                        }
                      </BlockStack>
                      <Button onClick={() => setStepOpen(stepOpen === index ? null : index)} icon={stepOpen === index ? ChevronUpIcon : ChevronDownIcon} />
                    </InlineStack>
                    <Collapsible open={stepOpen === index} key={index}>
                      <BlockStack gap="500">
                        <InlineGrid gap="500" columns="2fr 1fr 1fr" alignItems="end">
                          <TextField label="Internal Name" value={step.internalName} onChange={value => setLocalSequence({ ...localSequence, steps: localSequence.steps.map((step, i) => i === index ? { ...step, internalName: value } : step) })} />
                          <TextField label="Delay" value={step.delay} onChange={value => setLocalSequence({ ...localSequence, steps: localSequence.steps.map((step, i) => i === index ? { ...step, delay: value } : step) })} suffix="Days" />
                          <Button disabled={localSequence.steps.length === 1} onClick={() => {
                            setLocalSequence({ ...localSequence, steps: localSequence.steps.filter((_, i) => i !== index) })
                            setStepOpen(null)
                          }}>Remove Step</Button>
                        </InlineGrid>
                        <Divider />
                        <TextField label="Subject" value={step.subject} onChange={value => setLocalSequence({ ...localSequence, steps: localSequence.steps.map((step, i) => i === index ? { ...step, subject: value } : step) })} />
                        <InstructionsRichTextEditor value={step.body}
                          setValue={(value) => setLocalSequence({
                            ...localSequence,
                            steps: localSequence.steps.map((step, i) => i === index ? { ...step, body: value } : step)
                          })}
                          variables={variables}
                        />
                      </BlockStack>
                    </Collapsible>
                  </BlockStack>
                </Card>
              )
            })
          }
          <InlineStack align="end">
            <Button icon={PlusIcon} onClick={() => {
              setStepOpen(localSequence.steps.length)
              setLocalSequence({
                ...localSequence,
                steps: [...localSequence.steps, { subject: '', body: '', internalName: 'Step ' + (localSequence.steps.length + 1), delay: 3 }]
              })
            }}>Add Step</Button>
          </InlineStack>
        </BlockStack>
      </AppLayout>
    )
  }

  const filteredSequences = emailSequences?.filter(s => {
    if (sequenceType === 'All') return true;
    if (sequenceType === 'Invite Leads') return s?.template === 'Invite Leads';
    if (sequenceType === 'Invite To Campaign') return s?.template === 'Invite To Campaign';
    if (sequenceType === 'After Campaign Accepted') return s?.template === 'After Campaign Accepted';
    if (sequenceType === 'Custom') return s?.template === 'Custom';
    return true
  }) || []

  console.log('filteredSequences', filteredSequences, sequenceType)

  const table = (
    <Card padding="0">
      <ResourceList
        emptyState={<EmptyState
          heading="No Sequences Yet"
          action={{
            content: 'Create Sequence',
            icon: EmailFollowUpIcon,
            onAction: () => {
              setStage('chooseTemplate')
            }
          }}
        />}
        isFiltered={true}
        resourceName={resourceName}
        items={filteredSequences || []}
        renderItem={renderItem}
        showHeader={false}
      />
    </Card>
  )

  function renderItem(sequence) {
    const sent = sequence?.sent
    const opened = sequence?.opened ? (sequence?.opened / sequence?.sent * 100).toFixed(1) + '%' : '0%'
    const clicked = sequence?.clicked ? (sequence?.clicked / sequence?.sent * 100).toFixed(1) + '%' : '0%'
    const days = sequence?.steps?.length && sequence?.steps?.length > 1 ? (
      sequence?.steps?.reduce((acc, step) => {
        return acc + Number(step.delay);
      }, 0) + ' days'
    ) : 'One-Off'

    return (
      <ResourceItem
        id={sequence.id}
        onClick={() => {
          setStage('editSequence')
          setLocalSequence(sequence)
        }}
        verticalAlignment="center"
        media={
          <Icon source={EmailFollowUpIcon} color="base" />
        }
        accessibilityLabel={`View details for ${sequence.name}`}
        shortcutActions={selected ? [
          {
            content: <Button icon={EditIcon} onClick={removeSelected}>Change</Button>,
          }
        ] : showActions ? [
          /*
        {
          content: <Button icon={DuplicateMinor} plain />,
          accessibilityLabel: `Duplicate ${sequence.name}`,
          onAction: () => {
            onClickSequence({ ...sequence, id: null, clicked: 0, opened: 0, sent: 0, isDuplicate: true, name: sequence?.name + ' COPY' })
          }
        },
*/
        ] : []}
        persistActions
      >
        <InlineStack gap="200" align="space-between">
          <BlockStack inlineAlign="start">
            <Text variant="bodyMd" fontWeight="bold" as="span">
              <Link removeUnderline>
                {sequence.name}
              </Link>
            </Text>
            <InlineStack gap="100">
              <Text>{sequence.template} | </Text>
              <Text variant="bodyMd" color="subdued">
                {sequence.steps?.length === 1 ? 'One-Off' : `${sequence.steps?.length} Emails, ${days}`}
              </Text>
            </InlineStack>
          </BlockStack>
          {
            showStats ? (
              <Box paddingInlineEnd="16">
                <InlineGrid gap={smallTable ? "200" : "800"} columns="3">
                  <BlockStack inlineAlign="center">
                    <Text variant="bodyMd" fontWeight="semibold">{sent || 0}</Text>
                    <Text variant="bodyMd" color="subdued">
                      Sent Emails
                    </Text>
                  </BlockStack>
                  <BlockStack inlineAlign="center">
                    <Text variant="bodyMd" fontWeight="semibold">{opened}</Text>
                    <Text variant="bodyMd" color="subdued">
                      Open Rate
                    </Text>
                  </BlockStack>
                  <BlockStack inlineAlign="center">
                    <Text variant="bodyMd" fontWeight="semibold">{clicked}</Text>
                    <Text variant="bodyMd" color="subdued">
                      Click Rate
                    </Text>
                  </BlockStack>
                </InlineGrid>
              </Box>
            ) : null
          }
        </InlineStack>
      </ResourceItem>
    )
  }

  async function archiveSequence() {
    try {
      setArchiveLoading(true);
      /*
      await authFetch(`/api/emailSequences/${archiveAreYouSure.id}`, {
        method: 'DELETE',
      })
      */
      setArchiveAreYouSure(null);
      setToast({
        content: 'Archived Sequence',
      })
      refetchSequences();
    } catch (error) {
      setToast({
        content: 'Something went wrong. Please try again.',
        error: true,
      })
    } finally {
      setArchiveLoading(false);
    }
  }

  const archiveAreYouSureMarkup = (
    <Modal
      open={archiveAreYouSure}
      onClose={() => setArchiveAreYouSure(null)}
      title={`Really Archive "${archiveAreYouSure?.name}"?`}
      primaryAction={{
        content: "Yes, Archive",
        onAction: archiveSequence,
        loading: archiveLoading
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => setArchiveAreYouSure(null),
        }
      ]}>
      <Modal.Section>
        <Text>
          Archiving a sequence will make it invisible.
        </Text>
        <Text>
          It will not affect any contacts that have already been added to the sequence.
        </Text>
      </Modal.Section>
    </Modal>
  )


  return (
    <AppLayout>
      <BlockStack gap="300">
        <InlineStack align="space-between">
          <InlineStack align="start" gap="100" blockAlign="center">
            <InlineStack>
              <Icon source={EmailFollowUpIcon} />
            </InlineStack>
            <Text variant="headingLg">Outreach Sequences</Text>
          </InlineStack>
          <Button onClick={() => {
            setSequenceTemplateOverride('Hello Emails')
            setLocalSequence(sequenceTemplates['Hello Emails'])
            setStage('editSequence')
          }} icon={PlusIcon}>New Sequence</Button>
        </InlineStack>
        {archiveAreYouSureMarkup}
        {table}
      </BlockStack>
    </AppLayout>
  );
}
